import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  Copy,
  Wallet2,
  Coins,
  CirclePlus,
  Send,
  Gift,
  CircleDashed,
  ArrowDownLeft,
  ArrowUpRight,
  RotateCcw,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services";
import Cross_Icon from "../../assests/cross.svg";
// import PaymentPage from "../../components/PaymentPage";
import SquarePayment from "../../components/SquarePayment";
import { addBalancetowallet } from "../../services/apis";
import LeftArrow from "../../assests/left-arrow.svg";
import ErrorHandlerPage from "../../components/ErrorHandlerPage";

// ── Types ──────────────────────────────────────────────────────────────────────
type TxSource =
  | "order_payment"
  | "order_refund"
  | "gift_card_created"
  | "gift_card_redeemed"
  | "new_user_gift_card_redeemed"
  | "bonus_to_wallet_transfer"
  | "admin_top_up"
  | "stripe_payment";

interface WalletTxn {
  _id: string;
  amount: number;
  type: "credit" | "debit";
  walletType: "wallet" | "bonus_wallet";
  source: TxSource;
  description: string;
  balanceBefore: number;
  balanceAfter: number;
  orderId?: string;
  createdAt: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const SOURCE_LABELS: Record<TxSource, string> = {
  order_payment: "Order Payment",
  order_refund: "Order Refund",
  gift_card_created: "Gift Card Sent",
  gift_card_redeemed: "Gift Card Redeemed",
  new_user_gift_card_redeemed: "Welcome Gift",
  bonus_to_wallet_transfer: "Bonus Transfer",
  admin_top_up: "Wallet Top-up",
  stripe_payment: "Card Payment",
};

function formatTxDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }) + " · " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

enum PaymentType {
  Stripe = "stripe",
  Wallet = "wallet",
}

export const Wallet: React.FC = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [redloading, setredLoading] = useState(false);   // redeem gift card
  const [createLoading, setCreateLoading] = useState(false); // create/topup actions
  const [message, setMessage] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTopupModalOpen, setIsTopupModalOpen] = useState(false);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [disableGiftCardButton, setDisableGiftCardButton] = useState(false);
  const { user, setUser, currencySymbol, adminSquareApplicationId, adminSquareLocationId } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isBalancePaymentOpen, setIsBalancePaymentOpen] = useState(false);
  const [fatalError, setFatalError] = useState<{
    title: string;
    description: string;
  } | null>(null);

  // ── Transaction history state ──────────────────────────────────────────────
  const [txns, setTxns] = useState<WalletTxn[]>([]);
  const [txnsLoading, setTxnsLoading] = useState(false);
  const [txnsPage, setTxnsPage] = useState(1);
  const [txnsHasMore, setTxnsHasMore] = useState(true);
  const [txnsFilter, setTxnsFilter] = useState<"all" | "wallet" | "bonus_wallet">("all");

  const fetchTxns = useCallback(async (page: number, filter: typeof txnsFilter, reset = false) => {
    try {
      setTxnsLoading(true);
      const params: Record<string, any> = { page, limit: 15 };
      if (filter !== "all") params.walletType = filter;
      const res = await api.get("/wallet/transactions", { params });
      const incoming: WalletTxn[] = res.data.data || [];
      setTxns((prev) => reset ? incoming : [...prev, ...incoming]);
      setTxnsHasMore(page < (res.data.pages || 1));
    } catch {
      // silently fail — not critical
    } finally {
      setTxnsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setTxnsPage(1);
      fetchTxns(1, txnsFilter, true);
    }
  }, [user, txnsFilter, fetchTxns]);

  const handleLoadMoreTxns = () => {
    const next = txnsPage + 1;
    setTxnsPage(next);
    fetchTxns(next, txnsFilter, false);
  };

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user]);
  // Handler for preset buttons
  const handleAmountClick = (value: number) => {
    const walletBalance = user?.wallet?.balance ?? 0;
    

    if (value > walletBalance) {
     setDisableGiftCardButton(true)
    }

    setMessage(null);
    setAmount(value);
    setTimeout(() => {
      document
        .querySelectorAll('button[title*="Select"]')
        .forEach((btn: any) => {
          const btnValue = parseInt(btn.textContent?.replace(/[^0-9]/g, "") || "0");
          if (btnValue === value) {
            btn.style.background = "black";
            btn.style.transform = "scale(1.1)";
            btn.style.boxShadow = "black";
          } else {
            btn.style.background = "black";
            btn.style.transform = "scale(1)";
            btn.style.boxShadow = "none";
          }
        });
    }, 0);
  };

  const handleBalClick = (value: number) => {
    setBalance(value);
    setTimeout(() => {
      document
        .querySelectorAll('button[title*="Select"]')
        .forEach((btn: any) => {
          const btnValue = parseInt(btn.textContent?.replace(/[^0-9]/g, "") || "0");
          if (btnValue === value) {
            btn.style.background = "black";
            btn.style.transform = "scale(1.1)";
            btn.style.boxShadow = "black";
          } else {
            btn.style.background = "black";
            btn.style.transform = "scale(1)";
            btn.style.boxShadow = "none";
          }
        });
    }, 0);
  };

  // Handler for manual input (always keeps `$` prefix)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisableGiftCardButton(false);
    const walletBalance = user?.wallet?.balance ?? 0;
    const value = e.target.value;
    console.log("pricing raw:", value); // shows "$5" as you type

    // remove non-numeric characters
    const cleanedValue = value.replace(/[^0-9.]/g, "");
    console.log("pricing cleaned:", cleanedValue); // shows "5"

    const numericValue = parseFloat(cleanedValue || "0");

    if (numericValue > walletBalance) {
      setDisableGiftCardButton(true);
    }

    setAmount(numericValue);
  };

  const handleBalInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/[^0-9.]/g, "");
    const numericValue = parseFloat(cleanedValue || "0");

    setMessage(null);
    setBalance(numericValue || null);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove invalid characters and convert to uppercase
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");

    // Limit to 16 characters
    if (value.length > 16) value = value.slice(0, 16);

    // Add hyphens after every 4 characters
    const parts = [];
    for (let i = 0; i < value.length; i += 4) {
      parts.push(value.slice(i, i + 4));
    }
    value = parts.join("-");

    setCode(value);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const formatCodeWithHyphens = (code: string) => {
    const cleanCode = code.replace(/-/g, "");
    const parts = [];
    for (let i = 0; i < cleanCode.length; i += 4) {
      parts.push(cleanCode.slice(i, i + 4));
    }
    return parts.join("-");
  };

  const handleRedeem = async () => {
    if (!code) {
      setMessage("Please enter a valid gift card code.");
      return;
    }

    try {
      setredLoading(true);
      setMessage(null);

      const codeToSend = code.replace(/-/g, "");

      const res = await api.post(`/wallet/redeemGiftCard`, {
        code: codeToSend,
      });

      const data = res.data;

      if (
        data.wallet?.bonusWallet &&
        typeof data.wallet.bonusWallet.balance === "number"
      ) {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                bonusWallet: {
                  ...prev.bonusWallet,
                  balance: data.wallet.bonusWallet.balance,
                },
              }
            : prev,
        );
      }

      setCode(" ");
    } catch (err: any) {
      console.error("Gift card redeem request failed:", err);
      setMessage("Gift card redemption is currently unavailable.");
      setFatalError({
        title: "Unable to Redeem Gift Card",
        description:
          "We could not process your gift card redemption at this time.",
      });
    } finally {
      setredLoading(false);
    }
  };

  const handleCreateCard = async (type: PaymentType) => {
    if (!amount) {
      setMessage("Please enter an amount.");
      return;
    }
    const numericAmount = amount;
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setMessage("Enter a valid amount greater than 0.");
      return;
    }

    if (type === PaymentType.Wallet) {
      const walletBalance = user?.wallet?.balance ?? 0;
      if (numericAmount > walletBalance) {
        setMessage(
          `❌ Amount cannot exceed your wallet balance of ${currencySymbol}${walletBalance}.`,
        );
        return;
      }
    }

    try {
      setCreateLoading(true);
      setMessage(null);

      const res = await api.post(`/wallet/createGiftCard`, {
        amount: numericAmount,
        paymentType: type,
      });

      const data = res.data;

      if (typeof data.balance === "number") {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                wallet: { ...prev.wallet, balance: data.balance },
              }
            : prev,
        );
      }

      if (data) {
        setGeneratedCode(data.code);
        setIsCodeModalOpen(true);
      }

      setAmount(null);
    } catch (err: any) {
      console.error("Gift card creation request failed:", err);
      setMessage("Gift card creation is currently unavailable.");
      setFatalError({
        title: "Unable to Create Gift Card",
        description: "We could not create a gift card at this time.",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAddbalancetowallet = async () => {
    if (!balance) {
      console.log("Please enter an amount.");
      return;
    }

    try {
      setCreateLoading(true);
      setMessage(null);

      const res = await addBalancetowallet({
        amount: balance,
        userId: user?.id!,
      });

      if (res.data.wallet) {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                wallet: { ...prev.wallet, balance: res.data.wallet.balance },
              }
            : prev,
        );
      }
    } catch (err: any) {
      console.error("Wallet top-up request failed:", err);
      setMessage("Wallet top-up is currently unavailable.");
      setFatalError({
        title: "Unable to Top Up Wallet",
        description: "We could not complete your wallet top-up at this time.",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  if (fatalError) {
    return (
      <ErrorHandlerPage
        title={fatalError.title}
        description={fatalError.description}
        retryLabel="Back to Wallet"
        onRetry={() => {
          setFatalError(null);
          setMessage(null);
        }}
      />
    );
  }


  return (
    <div className="min-h-screen bg-white ">
      {/* Header Section */}
      <div className="bg-white  px-4 sm:px-6 py-4 ">
       <div className="max-w-4xl mx-auto px-2 sm:px-4">
  <div className="flex items-center gap-3 sm:gap-4">
    <button
      className="rounded-full flex items-center justify-center w-[35px] h-[35px] flex-shrink-0 border border-black/20 bg-white transition"
      onClick={() => navigate(-1)}
      aria-label="Go back"
      title="Return to previous page"
    >
      <img src={LeftArrow} alt="Back" className="w-4 h-4 dm-icon-invert" />
    </button>

    <h2 className="font-bold text-black text-xl sm:text-2xl leading-none m-0">
      Wallet
    </h2>
  </div>
</div>

      </div>

      <div className="max-w-4xl mx-auto p-4 pt-0 sm:p-6 pb-3 ">
        {/* Profile Card */}
        <div className="text-black px-[12px] sm:px-[15px] py-[10px] bg-white border-[1px] border-black/5 rounded-2xl ">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row justify-between w-full items-start sm:items-center gap-3 sm:gap-0">
              <div className="flex gap-3 sm:gap-4 justify-start items-center flex-row w-full sm:w-auto">
                  <div className="w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] flex-shrink-0 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold select-none" style={{ backgroundColor: 'var(--bw-black)', color: 'var(--bw-white)' }}>
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-[18px] sm:text-[20px] font-bold mb-0 truncate">
                    {user?.name || "User"}
                  </h2>
                  <p className="text-gray-600 text-[13px] sm:text-[15px] mb-0 truncate">
                    {user?.email}
                  </p>
                  <p className="text-gray-600 text-[13px] sm:text-[15px]  mb-0 ">
                    {user?.phonenumber}{" "}
                  </p>
                </div>
              </div>
              <div className="mt-0 text-gray-700 font-normal flex flex-col justify-end items-start w-full sm:w-auto">
                <span className="dm-wallet-balance-label mt-0 mb-0 text-[12px] sm:text-[13px] whitespace-nowrap">
                  Wallet Balance: {(user?.wallet?.balance ?? 0).toFixed(2)}
                </span>

                <span className="dm-wallet-balance-label text-[12px] sm:text-[13px] whitespace-nowrap">
                  Bonus Wallet Balance:{" "}
                  {(user?.bonusWallet?.balance ?? 0).toFixed(2)}
                </span>
              </div>
            </div>
            {/* <button className="text-orange-500 font-semibold hover:text-orange-600 transition">
                Edit
              </button> */}
          </div>
        </div>
      </div>
      {/* Center Section */}
      <div className="max-w-4xl mx-auto p-4 sm:p-6 pt-0 ">
        <div className="flex flex-col items-center  ">
          {/* {message && (
            <h1 className="mt-4 text-center text-sm font-semibold text-red-700">
              {message}
            </h1>
          )} */}

          <div className="text-black px-[16px] sm:px-[20px] py-[24px] sm:py-[30px] flex flex-col justify-between gap-5 sm:gap-7 bg-white w-full  border-[1px] border-black/5 rounded-[15px]">
            {/* User Info + Top-up */}
            <div className="flex items-center justify-between gap-3 mb-2 sm:mb-4 px-[16px] sm:px-[30px]">
              <div className="flex items-center min-w-0">
                <div className="bg-white rounded-lg border-[1px] border-black/30 p-2 sm:p-2.5 md:p-3 mr-3 sm:mr-3.5 md:mr-4 flex-shrink-0">
                  <Wallet2 className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-black" />
                </div>
                <div className="min-w-0">
                  <div className="dm-wallet-balance-label text-[11px] sm:text-[12px] text-black/70 font-normal sm:text-sm opacity-80">
                    Welcome,
                  </div>
                  <h1 className="font-semibold text-[15px] sm:text-base md:text-lg  mb-0 truncate">
                    {user?.name}
                  </h1>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsTopupModalOpen(true)}
                className="dm-wallet-cta flex-shrink-0 flex items-center gap-1.5 sm:gap-2 bg-main text-white font-semibold rounded-full px-3.5 sm:px-5 h-9 sm:h-10 text-[13px] sm:text-sm hover:scale-105 transition-transform"
              >
                <CirclePlus className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                Top-up
              </button>
            </div>
            {/* Balance Card */}
            <div className="bg-transparent border-[1px] border-black/20 rounded-[16px] mb-4 sm:mb-6 mx-[16px] sm:mx-[30px] w-auto sm:w-11/12">
             <div className="flex flex-row justify-between items-start sm:items-stretch w-full py-[20px] sm:py-[28px] px-[18px] sm:px-[23px] gap-3 sm:gap-4">
              {/* Wallet Balance Section */}
              <div className="flex flex-col">
                <p className="dm-wallet-balance-label text-[10px] sm:text-[11px] text-black/70 mb-1">
                  Wallet Balance
                </p>
                <h2 className="dm-wallet-balance-amount font-extrabold text-[20px] sm:text-[23px] md:text-[24px] text-black">
                  {currencySymbol}{(user?.wallet?.balance ?? 0).toFixed(2)}
                </h2>
              </div>

              {/* Bonus Section */}
              <div className="flex flex-col items-start sm:items-end justify-center">
                <div className="dm-wallet-cta bg-main text-white px-3 py-1 rounded-full flex items-center space-x-1 text-xs sm:text-sm">
                  <Coins className="dm-wallet-coin w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                  <span>{(user?.bonusWallet?.balance ?? 0).toFixed(2)}</span>
                </div>
                {(user?.bonusWallet?.pendingBalance ?? 0) > 0 && (
                  <p className="text-[10px] mt-1 text-amber-600 m-0">
                    + {currencySymbol}{(user?.bonusWallet?.pendingBalance ?? 0).toFixed(2)} pending
                  </p>
                )}
              </div>
            </div>
            </div>
            {/* Old action buttons (Top-up moved beside "Welcome"; Create/Redeem hidden — restore when needed)
            
            <div className="flex justify-center  gap-6 sm:gap-8 md:gap-10 max-w-3xl mx-auto">
              
              <button
                type="button"
                className="flex flex-col items-center"
                onClick={() => setIsTopupModalOpen(true)}
              >
                <button className="dm-wallet-cta h-10 w-10 bg-main text-white sm:h-12 sm:w-12 rounded-lg flex items-center justify-center hover:scale-105 transition-transform">
                  <CirclePlus className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <span className="dm-wallet-action-label text-[10px]  text-black/70 sm:text-xs mt-1">
                  Top-up
                </span>
                
              </button>

              
              <button
                type="button"
                className="flex flex-col items-center"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <button className="dm-wallet-cta h-10 w-10 sm:h-12 sm:w-12 bg-main text-white rounded-lg flex items-center justify-center hover:scale-105 transition-transform">
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <span className="dm-wallet-action-label text-[10px] text-black/70 sm:text-xs mt-1">
                  Create
                </span>
                
              </button>

              
              <button
                type="button"
                className="flex flex-col items-center"
                onClick={() => setIsRedeemModalOpen(true)}
              >
                <button className="dm-wallet-cta h-10 w-10 sm:h-12 sm:w-12 bg-main text-white rounded-lg flex items-center justify-center hover:scale-105 transition-transform">
                  <Gift className="w-4 h-4 sm:w-5 sm:h-5 " />
                </button>
                <span className="dm-wallet-action-label text-[10px] text-black/70 sm:text-xs mt-1">
                  Redeem
                </span>
                
              </button>

              
            </div>
            */}
          </div>
        </div>
      </div>

      {/* ── Transaction History ───────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-10">
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-4 border-b border-black/5">
            <h3 className="font-bold text-[16px] sm:text-[18px] text-black">
              Transaction History
            </h3>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 px-3 pt-4 pb-2">
            {(["all", "wallet", "bonus_wallet"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setTxnsFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  txnsFilter === f
                    ? "bg-black !text-white dark:text-black"
                    : "bg-black/5 text-black/60 hover:bg-black/10"
                }`}
              >
                {f === "all" ? "All" : f === "wallet" ? "Wallet" : "Bonus Wallet"}
              </button>
            ))}
          </div>

          {/* List */}
          <ul className="divide-y divide-black/5 p-0">
            {txns.length === 0 && !txnsLoading && (
              <li className="px-5 py-10 text-center text-sm text-black/40">
                No transactions yet.
              </li>
            )}

            {txns.map((tx) => {
              const isCredit = tx.type === "credit";
              const isRefund = tx.source === "order_refund";

              return (
                <li key={tx._id} className="flex items-start gap-3 sm:gap-4 px-3 py-4 hover:bg-black/[0.02] transition">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
                      isRefund
                        ? "bg-blue-50"
                        : isCredit
                        ? "bg-green-50"
                        : "bg-red-50"
                    }`}
                  >
                    {isRefund ? (
                      <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                    ) : isCredit ? (
                      <ArrowDownLeft className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                    ) : (
                      <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[13px] sm:text-[14px] text-black truncate">
                        {tx.description || SOURCE_LABELS[tx.source] || tx.source}
                      </span>
                      {/* Type badge */}
                      <span
                        className={`text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          isRefund
                            ? "bg-blue-100 text-blue-600"
                            : isCredit
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {isRefund ? "Refunded" : isCredit ? "Credited" : "Debited"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {/* Source chip */}
                      <span className="text-[10px] sm:text-[11px] text-black/50 bg-black/5 px-2 py-0.5 rounded-full">
                        {SOURCE_LABELS[tx.source] || tx.source}
                      </span>
                      {/* Wallet type chip */}
                      <span className="text-[10px] sm:text-[11px] text-black/50">
                        {tx.walletType === "wallet" ? "Main Wallet" : "Bonus Wallet"}
                      </span>
                    </div>

                    <div className="text-[10px] sm:text-[11px] text-black/40 mt-1">
                      {formatTxDate(tx.createdAt)}
                    </div>
                  </div>

                  {/* Amount + balance */}
                  <div className="text-right flex-shrink-0">
                    <div
                      className={`font-bold text-[14px] sm:text-[15px] ${
                        isRefund
                          ? "text-blue-600"
                          : isCredit
                          ? "text-green-600"
                          : "text-red-500"
                      }`}
                    >
                      {isCredit ? "+" : "−"}{currencySymbol}{(tx.amount ?? 0).toFixed(2)}
                    </div>
                    <div className="text-[10px] sm:text-[11px] text-black/40 mt-0.5">
                      Bal: {currencySymbol}{(tx.balanceAfter ?? 0).toFixed(2)}
                    </div>
                  </div>
                </li>
              );
            })}

            {/* Loading skeleton rows */}
            {txnsLoading &&
              Array.from({ length: 4 }).map((_, i) => (
                <li key={`sk-${i}`} className="flex items-center gap-4 px-5 py-4 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-black/5 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-black/5 rounded w-2/3" />
                    <div className="h-2 bg-black/5 rounded w-1/3" />
                  </div>
                  <div className="h-4 bg-black/5 rounded w-14" />
                </li>
              ))}
          </ul>

          {/* Load more */}
          {txnsHasMore && !txnsLoading && txns.length > 0 && (
            <div className="px-5 py-4 border-t border-black/5">
              <button
                onClick={handleLoadMoreTxns}
                className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-black/60 hover:text-black transition py-1"
              >
                <ChevronDown className="w-4 h-4" />
                Load more
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Code-Redeem-Modal */}

      {isRedeemModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 animate-fade-in p-4"
        >
          <div className="relative flex flex-col w-full max-w-3xl max-h-[90vh] rounded-2xl sm:rounded-3xl bg-white overflow-hidden animate-fade-in-up">
            <div className="bg-white flex  justify-between items-center w-full px-4 sm:px-9 py-4 sm:py-6  relative   border-b-[1px] border-black/20">
              <h2 className="dm-wallet-heading text-[17px] sm:text-[20px] font-bold text-black">
                Redeem Your Gift Card Here
              </h2>

              <button
                onClick={() => {
                  setIsRedeemModalOpen(false);
                  setMessage("");
                  setCode("");
                }}
                className=" hover:scale-110 transition"
              >
                <img
                  src={Cross_Icon}
                  alt="Close"
                  className="w-4 h-4 dm-icon-invert"
                />
              </button>
            </div>
            {/* Wallet Card */}
            <div className=" p-4 sm:p-5">
              <div
                className="bg-white
        rounded-[15px]   px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4  border-[1px] border-black/20 "
              >
                <Wallet2 className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 text-black" />

                <div className="min-w-0">
                  <p className="dm-wallet-balance-label text-black/70 text-xs sm:text-sm mb-0">
                    Your Bonus Wallet Balance
                  </p>
                  <h2 className="dm-wallet-balance-amount font-extrabold text-lg sm:text-xl  text-black">
                    {(user?.bonusWallet?.balance ?? 0).toFixed(2)}
                  </h2>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-5 pt-0 w-full">
              {message && (
                <h1 className=" text-center text-xs sm:text-sm font-semibold text-black">
                  {message}
                </h1>
              )}

              {/* Label */}
              <label
                htmlFor="giftCode"
                className="font-bold text-base sm:text-lg text-black block mb-2 mt-3"
              >
                Enter Your Unique Code
              </label>

              {/* Input */}
              <input
                id="giftCode"
                type="text"
                value={code}
                onChange={handleCodeChange}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                maxLength={19}
                className="text-base sm:text-xl text-left p-2.5 sm:p-3 w-full  bg-white rounded-xl 
        focus:outline-none  border-[1px] border-black/20
        transition-all duration-300 "
              />

              {/* Submit Button */}
              <button
                onClick={() => {
                  handleRedeem();
                }}
                disabled={redloading || !code}
                className={`dm-wallet-cta px-0 py-2.5 sm:py-3 bg-main text-white font-bold
            rounded-xl mt-4 sm:mt-6 w-full transition-all duration-300
            flex items-center justify-center gap-2 text-[14px] sm:text-lg
            ${
              redloading
                ? "opacity-60 cursor-not-allowed"
                : " hover:scale-[1.02]"
            }`}
              >
                {redloading ? "Processing..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Code-Created-Modal */}
      {isCodeModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in flex items-center justify-center p-4"
          style={{ paddingTop: "3rem" }}
        >
          <div
            className="relative flex flex-col items-center w-full max-w-3xl p-6 sm:p-8 rounded-2xl border-2
        border-main bg-white  overflow-hidden animate-fade-in-up"
          >
            {/* ✖️ Close Button */}
            <button
              onClick={() => setIsCodeModalOpen(false)}
              className="absolute top-4 right-4 text-black z-20 dm-wallet-close"
              aria-label="Close redeem modal"
            >
              ✕
            </button>

            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-white rounded-2xl pointer-events-none"></div>

            {/* Background illustration placeholder */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 opacity-10 pointer-events-none">
              <svg
                width="100"
                height="100"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="10"
                  y="20"
                  width="80"
                  height="60"
                  rx="8"
                  fill="#FF6B35"
                />
                <rect x="20" y="30" width="60" height="40" rx="4" fill="#FFF" />
                <circle cx="50" cy="50" r="10" fill="#FF6B35" />
              </svg>
            </div>

            {/* Icon and Title */}
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 z-10">
              <CreditCard className="w-8 h-8 sm:w-10 sm:h-10 text-black" />
              <h1 className="text-xl sm:text-3xl font-bold text-black text-center leading-tight">
                Here is Your Gift Card Code
              </h1>
            </div>
            {copySuccess && (
              <p className="relative z-10 text-green-600 text-center mt-2 font-semibold animate-fade-in text-sm sm:text-base">
                ✓ Copied to clipboard!
              </p>
            )}
            <div className="mb-4 sm:mb-6 w-full z-10">
              <div
                className="dm-giftcode-box text-base sm:text-xl p-3 sm:p-4 w-full border-2 border-black/30 rounded-xl cursor-pointer tracking-wider 
          flex items-center justify-center relative"
                title="Click to copy"
              >
                <span className="dm-giftcode-text mx-auto break-all">
                  {formatCodeWithHyphens(generatedCode)}
                </span>
                <Copy
                  onClick={handleCopyCode}
                  className="dm-giftcode-copy absolute right-3 sm:right-4 hover:scale-110 transform transition"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create-Giftcard Modal */}
      {isCreateModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 animate-fade-in p-4"
        >
          <div className="relative flex flex-col w-full max-w-3xl rounded-3xl bg-white overflow-hidden animate-fade-in-up">
            {/* Top Gradient Header */}

            <div className="bg-white flex  justify-between items-center w-full px-4 sm:px-9 py-4 sm:py-6  relative   border-b-[1px] border-black/20">
              <h2 className="dm-wallet-heading text-[20px] font-bold text-black">
                Create Your Gift Card
              </h2>

              <button
                onClick={() => setIsCreateModalOpen(false)}
                className=" hover:scale-110 transition"
              >
                <img
                  src={Cross_Icon}
                  alt="Close"
                  className="w-4 h-4 dm-icon-invert"
                />
              </button>
            </div>

            {/* Wallet Card */}
            <div className="p-4 sm:p-5 ">
              <div
                className=" bg-white
        rounded-[15px] mt-2  px-6 py-4 flex items-center gap-4  border-[1px] border-black/20 "
              >
                <Wallet2 className="w-10 h-10 text-black" />

                <div>
                  <p className="dm-wallet-balance-label text-[12px] text-black/70 text-sm mb-0">
                    Your Wallet Balance
                  </p>
                  <h2 className="dm-wallet-balance-amount font-extrabold text-[24px] text-black">
                    {(user?.wallet?.balance ?? 0).toFixed(2)}
                  </h2>
                </div>
              </div>
            </div>
            {/* Body */}
            <div className="p-4 sm:p-5 pt-0 w-full">
              {/* Label */}
              <label
                htmlFor="giftCode"
                className="font-bold text-lg text-black block mb-2 "
              >
                Enter Amount
              </label>

              {/* Input */}
              <input
                id="amount"
                type="text"
                value={amount === null ? "" : `${currencySymbol}${amount}`}
                onChange={handleInputChange}
                placeholder={`${currencySymbol}0`}
                className="text-xl text-left p-2.5 w-full  bg-white  rounded-xl 
        focus:outline-none 
        transition-all duration-300 border-[1px] border-black/20"
              />
              <div className="flex flex-wrap gap-2 sm:gap-4 mb-8 mt-4">
                {[50, 100, 200, 500].map((val) => (
                  <button
                    key={val}
                    onClick={() => handleAmountClick(val)}
                    className={`dm-wallet-cta flex items-center justify-center gap-1 px-2 py-2  bg-main text-white font-semibold rounded-xl hover:scale-105`}
                    title={`Select ${currencySymbol}${val}`}
                  >
                    <CircleDashed className="dm-wallet-coin w-4 h-4 text-yellow-400" />{currencySymbol}{val}
                  </button>
                ))}
              </div>
              <div className="flex justify-between gap-4">
                {/* Wallet */}
                <button
                  onClick={() => {
                    handleCreateCard(PaymentType.Wallet);
                    setIsCreateModalOpen(false);
                  }}
                  disabled={createLoading || !amount || disableGiftCardButton}
                  className={`dm-wallet-cta px-0 py-3  bg-main text-white font-bold
      rounded-xl mt-6 w-[90%] 
      flex items-center justify-center gap-2 text-lg
      ${
        createLoading
          ? "opacity-60 cursor-not-allowed"
          : "hover:scale-[1.02]"
      }`}
                >
                  {createLoading ? "Processing..." : "Pay with Wallet"}
                </button>

                {/* Stripe */}
                <button
                  onClick={() => {
                    setIsOpen(true);
                    setIsCreateModalOpen(false);
                  }}
                  disabled={createLoading || !amount}
                  className={`dm-wallet-cta px-0 py-3  bg-main text-white font-bold
      rounded-xl mt-6 w-[90%]
      flex items-center justify-center gap-2 text-lg
      ${
        createLoading
          ? "opacity-60 cursor-not-allowed"
          : " hover:scale-[1.02]"
      }`}
                >
                  {createLoading ? "Processing..." : "Pay with Square"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* topup modal */}
      {isTopupModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 animate-fade-in p-4"
        >
          <div className="relative flex flex-col w-full max-w-3xl  max-h-[90vh] rounded-2xl bg-white overflow-y-auto no-scrollbar animate-fade-in-up">
            {/* Top Gradient Header */}
            {/* <div className="bg-white flex items-center w-full px-9 py-6 relative  border-b-[1px] border-black/20">

      <button
        onClick={() => {setIsTopupModalOpen(false); setMessage("");}}
        className="absolute top-5 right-5 text-black hover:scale-110 transition"
      >
        <CircleX className="w-6 h-6" />
      </button>

      <h1 className="text-[20px] font-extrabold text-black text-left mt-4 mb-3">
        Topup Your Wallet
      </h1>
</div> */}
        <div className="bg-white flex  justify-between items-center w-full px-4 sm:px-9 py-4 sm:py-6  relative   border-b-[1px] border-black/20">
              <h2 className="dm-wallet-heading text-[20px] font-bold text-black">
                {" "}
                Topup Your Wallet
              </h2>

              <button
                onClick={() => {
                  setIsTopupModalOpen(false);
                  setMessage("");
                }}
                className=" hover:scale-110 transition"
              >
                <img
                  src={Cross_Icon}
                  alt="Close"
                  className="w-4 h-4 dm-icon-invert"
                />
              </button>
            </div>
            {/* Wallet Card */}
            <div className="p-4 sm:p-5">
              <div className="bg-white  rounded-[15px] mt-2  px-6 py-4 flex items-center gap-4  border-[1px] border-black/20 ">
                <Wallet2 className="w-10 h-10 text-black" />

                <div>
                  <p className="dm-wallet-balance-label text-[12px] text-black/70 text-sm mb-0">
                    Your Current Wallet Balance
                  </p>
                  <h2 className="dm-wallet-balance-amount font-extrabold text-[24px] text-black">
                    {(user?.wallet?.balance ?? 0).toFixed(2)}
                  </h2>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-5 pt-0  w-full">
              {/* Label */}
              <label
                htmlFor="giftCode"
                className="font-bold text-lg text-black block mb-2 "
              >
                Enter Amount
              </label>

              {/* Input */}
              <input
                id="Bal"
                type="text"
                value={balance === null ? "" : `${currencySymbol}${balance}`}
                onChange={handleBalInputChange}
                placeholder={`${currencySymbol}0`}
                className="text-xl text-left p-2.5 w-full  bg-white  rounded-xl 
        focus:outline-none 
        transition-all duration-300 border-[1px] border-black/20 "
              />
              <div className="flex flex-wrap gap-2 sm:gap-4 mb-8 mt-4">
                {[50, 100, 200, 500].map((balance) => (
                  <button
                    key={balance}
                    onClick={() => handleBalClick(balance)}
                    className={`dm-wallet-cta flex items-center justify-center gap-1 px-2 py-2 bg-main text-white font-semibold rounded-xl hover:scale-105 transition-all`}
                    title={`Select ${currencySymbol}${balance}`}
                  >
                    <CircleDashed className="dm-wallet-coin w-4 h-4 text-yellow-400" />{currencySymbol}
                    {balance}
                  </button>
                ))}
              </div>
              <div className="flex justify-center">
                {/* Submit Button */}
                <button
                  onClick={() => {
                    if (balance && balance > 0) {
                      setIsBalancePaymentOpen(true);
                    } else {
                      setMessage("Please enter an amount.");
                    }
                  }}
                  disabled={createLoading}
                  className={`dm-wallet-cta px-0 py-3 bg-main text-white font-bold
            rounded-xl mt-6 w-full
            flex items-center justify-center gap-2 text-lg
            ${
              createLoading
                ? "opacity-60 cursor-not-allowed"
                : "hover:scale-[1.02]"
            }`}
                >
                  {createLoading ? "Processing..." : "+Add Balance"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Stripe payment flow kept for reference */}
      {/* {amount && (
        <PaymentPage
          pricing={amount}
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setIsTopupModalOpen(false);
          }}
          handleOrder={() => handleCreateCard(PaymentType.Stripe)}
          productDescription="Buy Gift Card"
        />
      )} */}

      {isOpen && amount && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 animate-fade-in p-4">
          <div className="relative flex flex-col w-full max-w-3xl rounded-2xl bg-white overflow-hidden animate-fade-in-up">
            <div className="bg-white flex justify-between items-center w-full px-4 sm:px-9 py-4 sm:py-6 border-b-[1px] border-black/20">
              <h2 className="dm-wallet-heading text-[20px] font-bold text-black">
                Complete Payment
              </h2>

              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsTopupModalOpen(false);
                }}
                className="hover:scale-110 transition"
              >
                <img
                  src={Cross_Icon}
                  alt="Close"
                  className="w-4 h-4 dm-icon-invert"
                />
              </button>
            </div>

            <div className="p-4 sm:p-5">
              <SquarePayment
                pricing={amount}
                handleOrder={() => handleCreateCard(PaymentType.Stripe)}
                onSuccess={() => {
                  setIsOpen(false);
                  setIsTopupModalOpen(false);
                }}
                applicationId={adminSquareApplicationId}
                locationId={adminSquareLocationId}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stripe payment flow kept for reference */}
      {/* {balance && isBalancePaymentOpen && (
        <PaymentPage
          pricing={balance}
          isOpen={isBalancePaymentOpen}
          onClose={() => {
            setIsBalancePaymentOpen(false);
            setIsTopupModalOpen(false);
          }}
          handleOrder={() => handleAddbalancetowallet()}
          productDescription="Add Balance to Wallet"
        />
      )} */}

      {balance && isBalancePaymentOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 animate-fade-in p-4">
          <div className="relative flex flex-col w-full max-w-3xl rounded-2xl bg-white overflow-hidden animate-fade-in-up">
            <div className="bg-white flex justify-between items-center w-full px-4 sm:px-9 py-4 sm:py-6 border-b-[1px] border-black/20">
              <h2 className="dm-wallet-heading text-[20px] font-bold text-black">
                Add Balance Payment
              </h2>

              <button
                onClick={() => {
                  setIsBalancePaymentOpen(false);
                  setIsTopupModalOpen(false);
                }}
                className="hover:scale-110 transition"
              >
                <img
                  src={Cross_Icon}
                  alt="Close"
                  className="w-4 h-4 dm-icon-invert"
                />
              </button>
            </div>

            <div className="p-4 sm:p-5">
              <SquarePayment
                pricing={balance}
                handleOrder={() => handleAddbalancetowallet()}
                onSuccess={() => {
                  setIsBalancePaymentOpen(false);
                  setIsTopupModalOpen(false);
                }}
                applicationId={adminSquareApplicationId}
                locationId={adminSquareLocationId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
